import { useToast } from 'primevue/usetoast';
import { GET_STUDENTS } from '@/graphql';
import { extractGraphQLError } from '@/utils/graphqlError';
import type { CreateStudentInput, EditStudentInput, PromoteStudentsInput, MutationPreviewStudentImportArgs, MutationImportStudentsArgs } from '@/types/composition-functions';
import {
  useCreateStudentMutation,
  useEditStudentMutation,
  useDeleteStudentMutation,
  usePromoteStudentsMutation,
  usePreviewStudentImportMutation,
  useImportStudentsMutation,
} from '@/types/vue-apollo-helpers';

export function useStudentMutations() {
  const toast = useToast();

  const studentRefetchQueries = [{ query: GET_STUDENTS }];

  const { mutate: createStudentMutate, loading: creatingStudent, error: createStudentError } = useCreateStudentMutation({
    refetchQueries: studentRefetchQueries,
  });
  const { mutate: editStudentMutate, loading: editingStudent, error: editStudentError } = useEditStudentMutation({
    refetchQueries: studentRefetchQueries,
  });
  const { mutate: deleteStudentMutate, loading: deletingStudent, error: deleteStudentError } = useDeleteStudentMutation({
    refetchQueries: studentRefetchQueries,
  });
  const { mutate: promoteStudentsMutate, loading: promotingStudents, error: promoteStudentsError } = usePromoteStudentsMutation({
    refetchQueries: studentRefetchQueries,
  });
  const { mutate: previewImportMutate, loading: previewingImport, error: previewImportError } = usePreviewStudentImportMutation();
  const { mutate: importStudentsMutate, loading: importingStudents, error: importStudentsError } = useImportStudentsMutation({
    refetchQueries: studentRefetchQueries,
  });

  async function createStudent(createStudentInput: CreateStudentInput) {
    try {
      const { data } = (await createStudentMutate({
        createStudentInput,
      }))!;

      toast.add({ severity: 'success', summary: 'Success', detail: 'Student created successfully!', life: 3000 });

      return data?.createStudent;
    } catch (error) {
      // The backend's own reason (first field message), never the raw "Validation failed for the field [createStudent]."
      toast.add({ severity: 'error', summary: 'Couldn\'t admit student', detail: extractGraphQLError(error, 'Error creating student'), life: 5000 });
      throw error;
    }
  }

  async function editStudent(input: EditStudentInput) {
    try {
      const { data } = (await editStudentMutate({
        input,
      }))!;

      toast.add({ severity: 'success', summary: 'Success', detail: 'Student updated successfully!', life: 3000 });

      return data?.editStudent;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Couldn\'t update student', detail: extractGraphQLError(error, 'Error updating student'), life: 5000 });
      throw error;
    }
  }

  async function deleteStudent(studentId: string) {
    try {
      const { data } = (await deleteStudentMutate({ id: studentId }))!;

      if (data?.deleteStudent?.success) {
        toast.add({
          severity: 'success',
          summary: 'Deleted',
          detail: data.deleteStudent.message || 'Student deleted successfully',
          life: 3000,
        });

        return true;
      } else {
        toast.add({
          severity: 'warn',
          summary: 'Failed',
          detail: data?.deleteStudent?.message || 'Unable to delete student',
          life: 3000,
        });
        return false;
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: extractGraphQLError(error, 'An error occurred while deleting student'),
        life: 3000,
      });
      throw error;
    }
  }

  async function promoteStudents(input: PromoteStudentsInput) {
    try {
      const { data } = (await promoteStudentsMutate({ input }))!;

      if (data?.promoteStudents?.success) {
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: data.promoteStudents.message || 'Students promoted successfully',
          life: 3000,
        });
        return true;
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: data?.promoteStudents?.message || 'Failed to promote students',
          life: 3000,
        });
        return false;
      }
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'An error occurred during promotion'), life: 3000 });
      throw error;
    }
  }

  async function previewStudentImport(variables: MutationPreviewStudentImportArgs) {
    try {
      const response = await previewImportMutate(variables);
      return response?.data?.previewStudentImport ?? null;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Preview Error', detail: extractGraphQLError(error, 'Failed to preview import'), life: 3000 });
      throw error;
    }
  }

  async function importStudents(variables: MutationImportStudentsArgs) {
    try {
      const response = await importStudentsMutate(variables);
      const result = response?.data?.importStudents;

      if (!result) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'No response from server.', life: 3000 });
        return null;
      }

      // Queued path: large payloads run as a background job.
      // Caller should hand off student_import_id to the polling composable.
      if (result.queued) {
        toast.add({
          severity: 'info',
          summary: 'Import Queued',
          detail: `${result.total_rows} students queued for background import. You will see live progress here.`,
          life: 6000,
        });
        return result;
      }

      // Synchronous path
      if (result.successful && result.successful > 0) {
        toast.add({ severity: 'success', summary: 'Success', detail: `${result.successful} of ${result.total_rows} students imported successfully`, life: 3000 });
      }
      if (result.failed && result.failed > 0) {
        toast.add({ severity: 'warn', summary: 'Warning', detail: `${result.failed} rows failed to import`, life: 5000 });
      }

      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error importing students.'), life: 3000 });
      throw error;
    }
  }

  return {
    createStudent,
    creatingStudent,
    createStudentError,
    editStudent,
    editingStudent,
    editStudentError,
    deleteStudent,
    deletingStudent,
    deleteStudentError,
    promoteStudents,
    promotingStudents,
    promoteStudentsError,
    previewStudentImport,
    previewingImport,
    previewImportError,
    importStudents,
    importingStudents,
    importStudentsError,
  };
}
